// Build-time registry of every experiment defined in /experiments.
//
// Uses Vite's `import.meta.glob` so adding a new flag is a one-file addition —
// no manual wiring needed here. The sync script does its own filesystem scan
// (Node doesn't have import.meta.glob), so this file is runtime-only.
//
// The registry is the canonical source for:
//   - the union type of valid flag keys (FlagKey)
//   - the runtime list of definitions (ALL_EXPERIMENTS)
//   - the runtime list of mutual exclusion groups (ALL_GROUPS)
//
// New experiment? Drop a file in /experiments and it lights up everywhere.

import {
  isExperimentDefinition,
  isMutualExclusionGroup,
  type ExperimentDefinition,
  type MutualExclusionGroup,
} from './define';

// `eager: true` resolves modules synchronously at bundle time — no async waterfall.
// The glob matches every `.ts` file under /experiments. Test files (`*.test.ts`)
// and the groups file are filtered out below; default-exports are the experiment;
// named exports in `_groups.ts` are the groups.
const experimentModules = import.meta.glob<{ default?: unknown }>(
  '/experiments/*.ts',
  { eager: true },
);

const groupsModule = import.meta.glob<Record<string, unknown>>(
  '/experiments/_groups.ts',
  { eager: true },
);

function collectExperiments(): ReadonlyArray<ExperimentDefinition> {
  const out: ExperimentDefinition[] = [];
  for (const [path, mod] of Object.entries(experimentModules)) {
    // Skip the groups file — it has named exports, not a default.
    if (path.endsWith('/_groups.ts')) continue;
    // Skip test files. The glob can't pre-filter these so we drop them here
    // — otherwise a `welcome_v1.test.ts` next to `welcome_v1.ts` would crash
    // the registry on its first runtime read.
    if (/\.test\.[tj]sx?$/.test(path)) continue;
    const def = (mod as { default?: unknown }).default;
    if (def === undefined) continue;
    if (!isExperimentDefinition(def)) {
      throw new Error(
        `[experiments] ${path}: default export is not an ExperimentDefinition (did you forget to wrap it in defineExperiment()?)`,
      );
    }
    out.push(def);
  }
  // Stable order for deterministic enrichment / debugging.
  out.sort((a, b) => a.key.localeCompare(b.key));
  return out;
}

function collectGroups(): ReadonlyArray<MutualExclusionGroup> {
  const out: MutualExclusionGroup[] = [];
  for (const mod of Object.values(groupsModule)) {
    for (const value of Object.values(mod)) {
      if (isMutualExclusionGroup(value)) out.push(value);
    }
  }
  out.sort((a, b) => a.key.localeCompare(b.key));
  return out;
}

export const ALL_EXPERIMENTS: ReadonlyArray<ExperimentDefinition> = collectExperiments();
export const ALL_GROUPS: ReadonlyArray<MutualExclusionGroup> = collectGroups();

/** Map of `flagKey → definition` for O(1) lookup at runtime. */
export const EXPERIMENTS_BY_KEY: Readonly<Record<string, ExperimentDefinition>> = Object.freeze(
  Object.fromEntries(ALL_EXPERIMENTS.map((e) => [e.key, e])),
);

/**
 * Union of all valid flag keys. Used to constrain <Experiment flag="…"> at
 * compile time.
 *
 * NOTE: Because experiment modules are discovered via `import.meta.glob`, the
 * literal types of their keys are erased at the registry boundary. For
 * compile-time autocomplete on `<Experiment flag="…">`, import the literal key
 * directly from the experiment file (e.g. `_system_smoke_test`) and reference
 * it through the typed default export. The runtime string is always valid as
 * long as the file exists.
 */
export type FlagKey = string;

/** Throws if the key is not registered — defensive helper for the sync script. */
export function getExperiment(key: string): ExperimentDefinition {
  const def = EXPERIMENTS_BY_KEY[key];
  if (!def) {
    throw new Error(`[experiments] no experiment registered with key "${key}"`);
  }
  return def;
}
